import { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/modal.css';
import { PlayerAvatar } from './PlayerAvatar';
import { BACKEND_URL } from '../config';

interface Player {
  pseudo: string;
  pseudoDisplay: string;
  hasPhoto: boolean;
  photoUrl?: string | null;
  team?: { id: number; nom: string } | null;
}

interface Team {
  id: number;
  nom: string;
  members: string[];
  membersDisplay?: string; // Liste des pseudos séparés par virgule (capitaine en premier)
  photoUrl: string;
}

interface ExistingConversation {
  id: string;
  type: 'direct' | 'group' | 'legacy';
  participants: string[];
  name?: string;
  isInterTeam?: boolean;
  teamAId?: number;
  teamBId?: number;
}

interface NewConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateDirect: (targetPseudo: string) => void;
  onCreateGroup: (participants: string[], name: string, avatarEmoji: string, avatarColor: string) => void;
  onCreateInterTeam?: (otherTeamId: number) => void;
  currentPseudo: string;
  existingConversations?: ExistingConversation[];
  isCompet?: boolean; // Mode compétition
  userTeamId?: number | null; // ID de l'équipe de l'utilisateur
}

// Palette de couleurs pour les avatars de groupe (identique à GroupSettingsModal)
const AVATAR_COLORS = [
  '#128c7e', // Vert WhatsApp
  '#00a884', // Vert clair
  '#25d366', // Vert vif
  '#075e54', // Vert foncé
  '#34b7f1', // Bleu clair
  '#2196f3', // Bleu
  '#3f51b5', // Indigo
  '#673ab7', // Violet
  '#9c27b0', // Pourpre
  '#e91e63', // Rose
  '#f44336', // Rouge
  '#ff5722', // Orange foncé
  '#ff9800', // Orange
  '#ffc107', // Ambre
  '#795548', // Marron
  '#607d8b', // Gris bleu
];

// Emojis classiques pour les avatars de groupe (identique à GroupSettingsModal)
const AVATAR_EMOJIS = [
  // Visages
  '😀', '😎', '🤩', '🥳', '😇', '🤗', '🙂', '😊',
  // Coeurs et symboles
  '❤️', '💙', '💚', '💛', '💜', '🧡', '🖤', '🤍',
  // Animaux
  '🐶', '🐱', '🐼', '🦁', '🐯', '🦊', '🐰', '🐻',
  '🐸', '🐵', '🦄', '🐝', '🦋', '🐢', '🐬', '🦅',
  // Nature
  '🌸', '🌺', '🌻', '🌹', '🌷', '🍀', '🌲', '🌴',
  '⭐', '🌙', '☀️', '🌈', '❄️', '🔥', '💧', '⚡',
  // Sport & loisirs
  '⚽', '🏀', '🎾', '🎱', '🎯', '🎮', '🎲', '🎸',
  '🎤', '🎬', '📚', '✈️', '🚀', '⛵', '🏔️', '🏖️',
  // Nourriture
  '🍕', '🍔', '🍟', '🍩', '🍪', '🎂', '🍰', '🍫',
  '☕', '🍺', '🍷', '🥤', '🍎', '🍓', '🍉', '🍇',
  // Objets
  '💎', '👑', '🎁', '🏆', '🔔', '💡', '📷', '🎨',
  '🔮', '🧲', '💰', '🎈', '🎉', '🎊', '🪄', '✨',
  // Personnes et groupes
  '👥', '👨‍👩‍👧‍👦', '👯', '🤝', '💪', '🙌', '👏', '✌️',
];

export default function NewConversationModal({
  isOpen,
  onClose,
  onCreateDirect,
  onCreateGroup,
  onCreateInterTeam,
  currentPseudo,
  existingConversations = [],
  isCompet = false,
  userTeamId = null,
}: NewConversationModalProps) {
  const [mode, setMode] = useState<'direct' | 'group' | 'inter-team'>('direct');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Player[]>([]);
  const [teamSearchResults, setTeamSearchResults] = useState<Team[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);
  const [groupName, setGroupName] = useState('');
  const [groupEmoji, setGroupEmoji] = useState('👥');
  const [groupColor, setGroupColor] = useState('#128c7e');
  const [isSearching, setIsSearching] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sélectionne une émoticon et une couleur aléatoires
  const getRandomEmoji = () => AVATAR_EMOJIS[Math.floor(Math.random() * AVATAR_EMOJIS.length)];
  const getRandomColor = () => AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode('direct');
      setSearchQuery('');
      setSearchResults([]);
      setTeamSearchResults([]);
      setSelectedPlayers([]);
      setGroupName('');
      // Sélectionner une émoticon et une couleur aléatoires pour les nouveaux groupes
      setGroupEmoji(getRandomEmoji());
      setGroupColor(getRandomColor());
      setShowEmojiPicker(false);
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Trouver si une conversation existe déjà avec ce joueur
  const hasExistingConversation = useCallback((pseudo: string): boolean => {
    return existingConversations.some(c => 
      c.type === 'direct' && 
      c.participants.some(p => p.toLowerCase() === pseudo.toLowerCase())
    );
  }, [existingConversations]);

  // Search players with debounce
  const searchPlayers = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/players/search?q=${encodeURIComponent(query)}&limit=10&compet=${isCompet}`
      );
      const data = await response.json();
      
      // Filter out current user and already selected players (for group mode)
      // But keep players with existing conversations (for direct mode)
      const filtered = (data.players || []).filter(
        (p: Player) => 
          p.pseudo.toLowerCase() !== currentPseudo.toLowerCase() &&
          !selectedPlayers.some(sp => sp.pseudo.toLowerCase() === p.pseudo.toLowerCase())
      );
      
      setSearchResults(filtered);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [currentPseudo, selectedPlayers, isCompet]);

  // Search teams with debounce
  const searchTeams = useCallback(async (query: string) => {
    if (query.length < 2) {
      setTeamSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/teams/search?q=${encodeURIComponent(query)}&limit=10`
      );
      const data = await response.json();
      
      // Filter out user's own team and teams with existing inter-team conversations
      const filtered = (data.teams || []).filter((t: Team) => {
        // Exclure l'équipe de l'utilisateur
        if (t.id === userTeamId) return false;
        
        // Exclure les équipes avec lesquelles il y a déjà un groupe inter-équipes
        const hasExisting = existingConversations.some(c => 
          c.isInterTeam && (c.teamAId === t.id || c.teamBId === t.id)
        );
        return !hasExisting;
      });
      
      setTeamSearchResults(filtered);
    } catch (err) {
      console.error('Team search error:', err);
      setTeamSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [userTeamId, existingConversations]);

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      if (mode === 'inter-team') {
        searchTeams(query);
      } else {
        searchPlayers(query);
      }
    }, 300);
  };

  // Select a player
  const handleSelectPlayer = (player: Player) => {
    if (mode === 'direct') {
      // Direct message: create immediately
      onCreateDirect(player.pseudo);
    } else {
      // Group: add to selected list
      setSelectedPlayers(prev => [...prev, player]);
      setSearchQuery('');
      setSearchResults([]);
      searchInputRef.current?.focus();
    }
  };

  // Select a team (inter-team mode)
  const handleSelectTeam = (team: Team) => {
    if (onCreateInterTeam) {
      onCreateInterTeam(team.id);
    }
  };

  // Remove a selected player
  const handleRemovePlayer = (pseudo: string) => {
    setSelectedPlayers(prev => prev.filter(p => p.pseudo !== pseudo));
  };

  // Create group
  const handleCreateGroup = () => {
    if (selectedPlayers.length < 1 || !groupName.trim()) return;
    
    onCreateGroup(
      selectedPlayers.map(p => p.pseudo),
      groupName.trim(),
      groupEmoji,
      groupColor
    );
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Nouvelle discussion</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Mode selector */}
        <div className="mode-selector">
          <button
            className={`mode-button ${mode === 'direct' ? 'active' : ''}`}
            onClick={() => { setMode('direct'); setSearchQuery(''); setSearchResults([]); setTeamSearchResults([]); }}
          >
            👤 Discussion privée
          </button>
          <button
            className={`mode-button ${mode === 'group' ? 'active' : ''}`}
            onClick={() => { setMode('group'); setSearchQuery(''); setSearchResults([]); setTeamSearchResults([]); }}
          >
            👥 Nouveau groupe
          </button>
          {isCompet && userTeamId && (
            <button
              className={`mode-button ${mode === 'inter-team' ? 'active' : ''}`}
              onClick={() => { setMode('inter-team'); setSearchQuery(''); setSearchResults([]); setTeamSearchResults([]); }}
            >
              ⚔️ Entre équipes
            </button>
          )}
        </div>

        {/* Group options (only in group mode) */}
        {mode === 'group' && (
          <div className="group-options">
            <div className="group-avatar-section">
              <div className="avatar-preview-container">
                <div 
                  className="avatar-preview"
                  style={{ backgroundColor: groupColor }}
                >
                  {groupEmoji}
                </div>
                <button 
                  className="change-emoji-btn"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                >
                  {showEmojiPicker ? 'Fermer' : 'Changer l\'emoji'}
                </button>
              </div>
              
              {showEmojiPicker && (
                <div className="emoji-picker">
                  <div className="emoji-grid extended">
                    {AVATAR_EMOJIS.map((emoji, index) => (
                      <button
                        key={index}
                        className={`emoji-option ${emoji === groupEmoji ? 'selected' : ''}`}
                        onClick={() => setGroupEmoji(emoji)}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="color-picker-section">
                <label>Couleur de fond</label>
                <div className="color-picker">
                  {AVATAR_COLORS.map((color, index) => (
                    <button
                      key={index}
                      className={`color-option ${groupColor === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setGroupColor(color)}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            <input
              type="text"
              className="group-name-input"
              placeholder="Nom du groupe"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              maxLength={50}
            />
          </div>
        )}

        {/* Selected players (group mode) */}
        {mode === 'group' && selectedPlayers.length > 0 && (
          <div className="selected-players">
            {selectedPlayers.map(player => (
              <div key={player.pseudo} className="selected-player-chip">
                <span>{player.pseudoDisplay}</span>
                <button
                  className="chip-remove"
                  onClick={() => handleRemovePlayer(player.pseudo)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Search input */}
        <div className="search-container">
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder={
              mode === 'direct' 
                ? "Rechercher un joueur..." 
                : mode === 'group'
                ? "Ajouter des participants..."
                : "Rechercher une équipe..."
            }
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {isSearching && <div className="search-spinner" />}
        </div>

        {/* Search results */}
        <div className="search-results">
          {/* Mode joueurs (direct/group) */}
          {mode !== 'inter-team' && (
            <>
              {searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                <div className="no-results">
                  Aucun joueur trouvé pour "{searchQuery}"
                </div>
              )}
              {searchResults.map(player => {
                const hasConv = mode === 'direct' && hasExistingConversation(player.pseudo);
                return (
                  <div
                    key={player.pseudo}
                    className="search-result-item"
                    onClick={() => handleSelectPlayer(player)}
                  >
                    <PlayerAvatar pseudo={player.pseudo} size={40} photoUrl={player.photoUrl} />
                    <div className="player-info">
                      <div className="player-name-row">
                        <span className="player-name">{player.pseudoDisplay}</span>
                        {hasConv && <span className="player-badge existing">💬</span>}
                      </div>
                      {player.team && (
                        <span className="player-team">{player.team.nom}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Mode équipes (inter-team) */}
          {mode === 'inter-team' && (
            <>
              {teamSearchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                <div className="no-results">
                  Aucune équipe trouvée pour "{searchQuery}"
                </div>
              )}
              {teamSearchResults.map(team => (
                <div
                  key={team.id}
                  className="search-result-item team-item"
                  onClick={() => handleSelectTeam(team)}
                >
                  <div className="team-avatar">
                    <img 
                      src={team.photoUrl} 
                      alt={team.nom}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).parentElement!.classList.add('fallback');
                      }}
                    />
                    <span className="team-avatar-fallback">⚔️</span>
                  </div>
                  <div className="player-info">
                    <div className="player-name-row">
                      <span className="player-name">{team.nom}</span>
                    </div>
                    <span className="player-team team-members-list" title={team.members.join(', ')}>
                      {team.membersDisplay || team.members.join(', ')}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Create group button */}
        {mode === 'group' && (
          <div className="modal-footer">
            <button
              className="create-group-button"
              disabled={selectedPlayers.length < 1 || !groupName.trim()}
              onClick={handleCreateGroup}
            >
              Créer le groupe ({selectedPlayers.length + 1} participants)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
